import React, { Component, Fragment } from 'react';
import {
  Button,
  Grid,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
  TextField,
  Paper,
  withStyles
} from '@material-ui/core';

import debounce from 'lodash/debounce';
import { get, defaultTo, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { addField, translate, FieldTitle } from 'react-admin';

import { ChevronLeftIcon } from '../../themes/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../themes/icons/ChevronRightIcon';
import { ChevronDownIcon } from '../../themes/icons/ChevronDownIcon';
import AutocompleteInput from '../forked/input/AutocompleteInput';
import { PendingIndicatorIcon } from '../../themes/icons/PendingIndicator';
import TagFilter from './TagFilter';

import doubleListStyles from '../../themes/utils/doubleListStyles';

const TagField = ({ tags, classes }) => {
  if (tags.length > 0) {
    return tags.map(tag => {
      return (
        <span key={tag.id} className={classes}>
          {tag.value}
        </span>
      );
    });
  }
  return null;
};

/**
 * An Input component for a select box, using an array of objects for the options
 */
class DoubleListArrayInput extends Component {
  /*
   * Using state to bypass a redux-form comparison but which prevents re-rendering
   * @see https://github.com/erikras/redux-form/issues/2456
   */
  state = {
    value: this.props.input.value,
    leftSelection: [],
    rightSelection: [],
    groupsCollapsed: {},
    selectedTags: [],
    fundInvSelectedTags: []
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.input.value !== prevState.value) {
      return {
        value: nextProps.input.value || []
      };
    }
    return null;
  }

  componentWillUnmount() {
    this.handleItemSearch.cancel();
    this.handleSelectedItemSearch.cancel();
  }

  render() {
    //NOSONAR javascript:S3776
    const self = this;
    const {
      choices,
      groups,
      filterByGroup,
      filterByGroupLabel,
      classes,
      meta,
      optionValue,
      optionText,
      isRequired,
      label,
      source,
      resource,
      additionalActions,
      showNotification,
      readOnly = false,
      allowTagFilter,
      tagsList,
      fundInvTagsList
    } = this.props;
    const {
      itemSearch = '',
      selectedItemSearch = '',
      filterOnGroup = -1,
      selectedTags = [],
      fundInvSelectedTags = []
    } = this.state;

    const displayTagFilter = allowTagFilter && tagsList.length > 0;
    const displayFundInvTagFilter =
      allowTagFilter && fundInvTagsList?.length > 0;

    const groupFilter =
      groups && groups.length > 1 ? this.sortChoices(groups) : null;
    const allowFilterByGroup = groupFilter && filterByGroup;

    const chevronStyle = {
      fontWeight: '900',
      color: '#2962FF'
    };

    if (typeof meta === 'undefined') {
      throw new Error(
        "The SelectInput component wasn't called within a redux-form <Field>. Did you decorate it and forget to add the addField prop to your component? See https://marmelab.com/react-admin/Inputs.html#writing-your-own-input-component for details."
      );
    }
    const { touched, error } = meta;

    // Value field (customizable in props)
    return (
      <Grid
        container
        justifyContent="space-between"
        classes={{ container: classes.containerClass }}
      >
        <Grid
          item
          xs={5}
          className={classes.gridLarge}
          data-testid="leftList"
          style={{ height: showNotification ? '95%' : '100%' }}
        >
          <Grid item xs={12} className={classes.itemGrid}>
            <InputLabel className={classes.searchLabel} htmlFor={source} shrink>
              <FieldTitle
                label={'Available ' + label}
                source={source}
                resource={resource}
              />
            </InputLabel>
          </Grid>
          <Grid container justifyContent="center" spacing={2}>
            <Grid
              item
              xs={this.getSize(
                allowFilterByGroup,
                displayTagFilter,
                displayFundInvTagFilter
              )}
            >
              <TextField
                margin="dense"
                placeholder={`Search ${label}...`}
                onChange={e => this.handleItemSearch(e.target.value)}
                id="search-input-left"
              />
            </Grid>
            {allowFilterByGroup && (
              <Grid
                item
                xs={displayTagFilter || displayFundInvTagFilter ? 4 : 6}
              >
                <AutocompleteInput
                  source="groupId"
                  label={false}
                  optionText="name"
                  autoComplete="off"
                  showArrowDown={true}
                  clientSideFilterOnly={true}
                  choices={groupFilter}
                  emptyText={filterByGroupLabel}
                  placeholder={filterByGroupLabel}
                  onChange={value => this.setState({ filterOnGroup: value })}
                  variant="standard"
                  options={{
                    suggestionsContainerProps: {
                      className: classes.autocompleteSuggestions
                    }
                  }}
                />
              </Grid>
            )}
            {(displayTagFilter || displayFundInvTagFilter) && (
              <Grid item xs={allowFilterByGroup ? 4 : 6}>
                <TagFilter
                  source={source}
                  tagsList={tagsList}
                  selectedTags={selectedTags}
                  onChange={e => this.setState({ selectedTags: e })}
                  fundInvTagsList={fundInvTagsList}
                  fundInvSelectedTags={fundInvSelectedTags}
                  onChangeFundInv={e =>
                    this.setState({ fundInvSelectedTags: e })
                  }
                  displayTagFilter={displayTagFilter}
                  displayFundInvTagFilter={displayFundInvTagFilter}
                />
              </Grid>
            )}
          </Grid>

          <Paper className={classes.paper}>
            <List component="nav" className={classes.list}>
              {this.group(
                choices.filter(choice => {
                  const value =
                    typeof optionValue === 'function'
                      ? optionValue(choice)
                      : get(choice, optionValue);
                  const text =
                    typeof optionText === 'function'
                      ? optionText(choice)
                      : defaultTo(get(choice, optionText), '');
                  if (
                    filterOnGroup &&
                    filterOnGroup !== -1 &&
                    filterOnGroup !== choice[this.props.groupBy]
                  ) {
                    return false;
                  }

                  if (
                    !this.compareProvidedTagsToSelectedTags(choice, source) &&
                    (selectedTags.length > 0 || fundInvSelectedTags.length > 0)
                  ) {
                    return false;
                  }

                  return (
                    !self.state.value.includes(value) &&
                    text.toLowerCase().includes(itemSearch.toLowerCase())
                  );
                })
              ).map(choice => this.renderListItem(choice, true))}
            </List>
          </Paper>
        </Grid>
        <Grid
          item
          xs={1}
          direction="column"
          alignItems="center"
          justifyContent="center"
          container
          className={classes.gridSmall}
          style={{ height: showNotification ? '95%' : '100%' }}
        >
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            onClick={this.handleAddClick}
            disabled={readOnly}
            id="chevron-right"
          >
            <ChevronRightIcon style={chevronStyle} />
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.separateButton}
            onClick={this.handleAddAllClick}
            disabled={readOnly}
            id="double-chevron-right"
          >
            <ChevronRightIcon style={chevronStyle} />
            <ChevronRightIcon style={chevronStyle} />
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            onClick={this.handleRemoveClick}
            disabled={readOnly}
            id="chevron-left"
          >
            <ChevronLeftIcon style={chevronStyle} />
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            onClick={this.handleRemoveAllClick}
            disabled={readOnly}
            id="double-chevron-left"
          >
            <ChevronLeftIcon style={chevronStyle} />
            <ChevronLeftIcon style={chevronStyle} />
          </Button>
        </Grid>
        <Grid
          item
          xs={5}
          className={classes.gridLarge}
          data-testid="rightList"
          style={{ height: showNotification ? '95%' : '100%' }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={8}>
              <InputLabel
                className={classes.searchLabel}
                htmlFor={source}
                shrink
              >
                <FieldTitle
                  label={'Selected ' + label}
                  source={source}
                  resource={resource}
                  isRequired={isRequired}
                />
              </InputLabel>
            </Grid>
            <Grid item xs={4} className={classes.itemsNumberGrid}>
              <FieldTitle
                label={`${self.state.value.length} of ${choices.length}`}
              />
            </Grid>
          </Grid>
          <TextField
            className={classes.searchBox}
            margin="dense"
            placeholder={`Search ${label}...`}
            onChange={e => this.handleSelectedItemSearch(e.target.value)}
            id="search-input-right"
          />
          <Paper className={classes.paper}>
            <List component="nav" className={classes.list}>
              {self.state.value.length > 0 ? (
                this.group(
                  choices.filter(choice => {
                    const value =
                      typeof optionValue === 'function'
                        ? optionValue(choice)
                        : get(choice, optionValue);
                    const text =
                      typeof optionText === 'function'
                        ? optionText(choice)
                        : defaultTo(get(choice, optionText), '');
                    return (
                      self.state.value.includes(value) &&
                      text
                        .toLowerCase()
                        .includes(selectedItemSearch.toLowerCase())
                    );
                  })
                ).map(choice => this.renderListItem(choice, false))
              ) : (
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  className={classes.fullHeight}
                >
                  <Grid item>
                    <Typography variant="caption" align="center">
                      No {label} selected
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </List>
          </Paper>
          <Grid item xs={12} className={classes.itemGrid}>
            {touched && error ? (
              <FormHelperText error>{error}</FormHelperText>
            ) : (
              <FormHelperText />
            )}
          </Grid>
          <Grid>{additionalActions ? additionalActions : <></>}</Grid>
        </Grid>
        {showNotification ? showNotification : <></>}
      </Grid>
    );
  }

  /**
   * Create list item from choice
   * @param {*} choice
   * @param {*} left true if rendering left item, false for rendering right item
   * @param inFamily
   */
  renderListItem(choice, left, inFamily = false) {
    let { classes, readOnly = false, label } = this.props;

    if (choice.children !== undefined) {
      // Group Entry
      return this.renderGroup(choice, left);
    }
    // Value field (customizable in props)
    const { optionValue } = this.props;
    const value =
      typeof optionValue === 'function'
        ? optionValue(choice)
        : get(choice, optionValue);
    const selected = left
      ? this.state.leftSelection.includes(value)
      : this.state.rightSelection.includes(value);
    return (
      <ListItem
        button
        key={value}
        onClick={
          !readOnly
            ? () =>
                left
                  ? this.handleLeftItemSelected(value)
                  : this.handleRightItemSelected(value)
            : undefined
        }
        onDoubleClick={
          !readOnly
            ? () =>
                left
                  ? this.handleLeftItemDoubleClick(value)
                  : this.handleRightItemDoubleClick(value)
            : undefined
        }
        className={selected ? classes.selected : classes.item}
        style={inFamily ? { paddingLeft: '25px' } : {}}
      >
        <div className={classes.pendingResourceWrapper}>
          {label === 'Funds' && choice.status === 'PENDING' && (
            <PendingIndicatorIcon title="Pending Fund" />
          )}
          <ListItemText
            primary={this.renderListItemLabel(choice, true)}
            className={classes.itemLabel}
            style={
              label === 'Funds' && choice.status !== 'PENDING'
                ? { paddingLeft: '16px' }
                : {}
            }
          />
        </div>
      </ListItem>
    );
  }

  sortChoices(choices) {
    return choices.sort((a, b) => {
      return this.getSortField(a)
        .replace(/[^\w\s]/gi, '')
        .localeCompare(this.getSortField(b).replace(/[^\w\s]/gi, ''));
    });
  }

  /**
   * Group list items.
   * @param {*} choices
   */
  group(choices) {
    const groupsDict = {};
    const tree = choices.reduce((list, choice) => {
      const groupId = choice[this.props.groupBy];
      if (groupId !== undefined && groupId !== null) {
        let group = groupsDict[groupId];
        if (group === undefined) {
          group = {
            groupId,
            groupLabel: this.getGroupLabel(groupId, choice),
            children: [choice]
          };
          groupsDict[groupId] = group;
          list.push(group);
        } else {
          group.children.push(choice);
        }
      } else {
        list.push(choice);
      }
      return list;
    }, []);

    return this.sortChoices(tree);
  }

  getSortField = choice => {
    const { optionText } = this.props;
    return choice.groupLabel
      ? choice.groupLabel
      : typeof optionText === 'function'
      ? optionText(choice)
      : defaultTo(get(choice, optionText), '');
  };

  /**
   * Render list item label
   */
  renderListItemLabel = (choice, includeId) => {
    const {
      classes,
      optionText,
      optionValue,
      translate,
      translateChoice,
      source,
      allowTagFilter
    } = this.props;
    if (React.isValidElement(optionText))
      return React.cloneElement(optionText, {
        record: choice
      });
    const choiceName =
      typeof optionText === 'function'
        ? optionText(choice)
        : defaultTo(get(choice, optionText), '');

    if (includeId) {
      const choiceId =
        typeof optionValue === 'function'
          ? optionValue(choice)
          : get(choice, optionValue);

      // Get tags associated to a fund or an investor (choice)
      const tagArray = [];
      const tagType = source === 'investortags' ? 'investorTags' : 'fundTags';
      Object.entries(choice[tagType] ? choice[tagType] : {}).forEach(
        ([key, value]) => {
          tagArray.push({ id: `${key}`, value: `${value}` });
        }
      );
      const fundInvTagsArray = [];
      Object.entries(
        choice['fundInvestorTags'] ? choice['fundInvestorTags'] : {}
      ).forEach(([key, value]) => {
        fundInvTagsArray.push({ id: `${key}`, value: `${value}` });
      });

      return translateChoice ? (
        <div className={classes.itemLabelText}>
          {translate(choiceName, { _: choiceName })}
          <div className={classes.itemId}>
            <span>(ID {choiceId})</span>
            {allowTagFilter && (
              <>
                <TagField tags={tagArray} classes={classes.tag} />
                {fundInvTagsArray.length > 0 && (
                  <TagField tags={fundInvTagsArray} classes={classes.tag} />
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className={classes.itemLabelText}>
          {choiceName}
          <div className={classes.itemId}>
            <span>(ID {choiceId})</span>
            {allowTagFilter && (
              <>
                <TagField tags={tagArray} classes={classes.tag} />
                {fundInvTagsArray.length > 0 && (
                  <TagField tags={fundInvTagsArray} classes={classes.tag} />
                )}
              </>
            )}
          </div>
        </div>
      );
    }
    return translateChoice
      ? translate(choiceName, { _: choiceName })
      : choiceName;
  };

  /**
   * Render goup of choices
   */
  renderGroup(choice, left) {
    let { classes } = this.props;
    return (
      <Fragment key={choice.groupId}>
        <ListItem className={classes.item}>
          <ListItemText
            disableTypography
            primary={
              <Grid container className={classes.root}>
                <Grid
                  item
                  onClick={() => this.toggleGroup(choice)}
                  data-testid="group"
                  id={choice.groupId}
                >
                  <div className={classes.groupChevron}>
                    {this.state.groupsCollapsed[choice.groupId] ? (
                      <ChevronRightIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </div>
                </Grid>
                <Grid item xs={11}>
                  <Typography variant="body1" component="div">
                    {choice.groupLabel}
                    {this.props.fundInvestorTag ? (
                      <div className={classes.itemId}>
                        (ID {choice.groupId})
                        <span className={classes.tag}>
                          {this.props.fundInvestorTag.name}
                        </span>
                      </div>
                    ) : (
                      <div className={classes.itemId}>(ID {choice.groupId}</div>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            }
          />
        </ListItem>
        {!this.state.groupsCollapsed[choice.groupId] &&
          choice.children.map(child => this.renderListItem(child, left, true))}
      </Fragment>
    );
  }

  /**
   * Toggle group (expanded / collapsed)
   * @param {*} choice
   */
  toggleGroup = choice => {
    let groupsCollapsed = { ...this.state.groupsCollapsed };
    groupsCollapsed[choice.groupId] = !groupsCollapsed[choice.groupId];
    this.setState({ groupsCollapsed });
  };

  /**
   * Get label from groups data
   * @param {*} groupId
   * @param choice
   */
  getGroupLabel(groupId, choice) {
    const { groups } = this.props;
    if (groups) {
      if (typeof groups === 'function') {
        return groups(choice);
      } else if (Array.isArray(groups)) {
        const idKey = this.props.groupId || 'id';
        const group = this.props.groups.find(group => group[idKey] === groupId);
        if (group !== undefined && this.props.groupLabel !== undefined) {
          return group[this.props.groupLabel];
        }
      }
    }
    return groupId;
  }

  /**
   * Double click on left list item => add to value list
   */
  handleLeftItemDoubleClick = value => {
    let valueList = [...this.state.value];
    valueList = valueList.concat(value);
    this.setState({ value: valueList });

    this.handleChange(valueList);
  };

  /**
   * Double click on right list item => remove from value list
   */
  handleRightItemDoubleClick = value => {
    const valueList = [...this.state.value];
    valueList.splice(value.indexOf(value), 1);
    this.setState({ value: valueList });

    this.handleChange(valueList);
  };

  /**
   * Click on left list item => add to left selected list
   */
  handleLeftItemSelected = value => {
    const array = this.state.leftSelection;
    if (array.includes(value)) {
      array.splice(array.indexOf(value), 1);
    } else {
      array.push(value);
    }
    this.setState({ leftSelection: array });
  };

  /**
   * Click on right list item => add to right selected list
   */
  handleRightItemSelected = value => {
    const array = this.state.rightSelection;
    if (array.includes(value)) {
      array.splice(array.indexOf(value), 1);
    } else {
      array.push(value);
    }
    this.setState({ rightSelection: array });
  };

  /**
   * Click on ">" middle button
   */
  handleAddClick = () => {
    const selected = this.state.leftSelection;
    const value = this.state.value.concat(selected);
    this.setState({ leftSelection: [], value });

    this.handleChange(value);
  };

  /**
   * Click on ">>" middle button
   */
  handleAddAllClick = () => {
    const { optionValue, optionText, choices, source } = this.props;
    const {
      itemSearch = '',
      value = [],
      filterOnGroup,
      selectedTags = [],
      fundInvSelectedTags = []
    } = this.state;

    let filteredChoices = choices;
    if (itemSearch || filterOnGroup || selectedTags || fundInvSelectedTags) {
      // Add only items filtered by search
      filteredChoices = choices.filter(choice => {
        const text =
          typeof optionText === 'function'
            ? optionText(choice)
            : defaultTo(get(choice, optionText), '');

        if (value.includes(choice.id)) {
          // Value contains the already selected one so any value included in value should not be filtered
          return true;
        }

        if (
          filterOnGroup &&
          filterOnGroup !== -1 &&
          filterOnGroup !== choice[this.props.groupBy]
        ) {
          return false;
        }

        if (
          !this.compareProvidedTagsToSelectedTags(choice, source) &&
          (selectedTags.length > 0 || fundInvSelectedTags.length > 0)
        ) {
          return false;
        }

        return text.toLowerCase().includes(itemSearch.toLowerCase());
      });
    }

    let newValue = filteredChoices.map(choice => {
      const value =
        typeof optionValue === 'function'
          ? optionValue(choice)
          : get(choice, optionValue);
      return value;
    });
    if (itemSearch) {
      newValue = value.concat(newValue);
    }
    this.setState({ leftSelection: [], value: newValue });
    this.handleChange(newValue);
  };

  /**
   * Click on "<" middle button
   */
  handleRemoveClick = () => {
    let value = this.state.value.slice(0);
    const selected = this.state.rightSelection;
    selected.forEach(s => {
      value.splice(value.indexOf(s), 1);
    });
    this.setState({ rightSelection: [], value });

    this.handleChange(value);
  };

  /**
   * Click on "<<" middle button
   */
  handleRemoveAllClick = () => {
    const { choices, optionText } = this.props;
    const { value, selectedItemSearch } = this.state;

    if (selectedItemSearch && value && value.length > 0) {
      const valueList = value.filter(val => {
        const choice = choices.filter(choice => choice.id === val)[0];
        const text =
          typeof optionText === 'function'
            ? optionText(choice)
            : defaultTo(get(choice, optionText), '');
        return !text.toLowerCase().includes(selectedItemSearch.toLowerCase());
      });
      this.setState({ rightSelection: [], value: valueList });
      this.handleChange(valueList);
    } else {
      this.setState({ rightSelection: [], value: [] });
      this.handleChange([]);
    }
  };

  handleChange = value => {
    this.props.input.onChange(value);
    // HACK: For some reason, redux-form does not consider this input touched without calling onBlur manually
    this.props.input.onBlur(value);
    if (this.props.checkJapanFund && this.props.handleJapanFund) {
      this.props.checkJapanFund(value, this.props.handleJapanFund);
    }
  };

  /**
   * Check if provided tags contains at least one of the selected tag
   */
  compareProvidedTagsToSelectedTags = (choice, source) => {
    // build an array of tag ids associated to a fund or an investor
    const tagIds = [];
    let tagType = source === 'investortags' ? 'investorTags' : 'fundTags';
    Object.entries(choice[tagType] ? choice[tagType] : {}).forEach(([key]) => {
      tagIds.push(parseInt(key));
    });

    const fundInvTagIds = [];
    if (!isEmpty(this.state.fundInvSelectedTags)) {
      Object.entries(
        choice['fundInvestorTags'] ? choice['fundInvestorTags'] : {}
      ).forEach(([key]) => {
        fundInvTagIds.push(parseInt(key));
      });
      if (isEmpty(this.state.selectedTags)) {
        return this.state.fundInvSelectedTags.some(elem =>
          fundInvTagIds.includes(elem)
        );
      } else {
        return (
          this.state.fundInvSelectedTags.some(elem =>
            fundInvTagIds.includes(elem)
          ) && this.state.selectedTags.some(elem => tagIds.includes(elem))
        );
      }
    }

    return this.state.selectedTags.some(elem => tagIds.includes(elem));
  };

  getSize = (allowFilterByGroup, displayTagFilter, displayFundInvTagFilter) => {
    if (allowFilterByGroup && (displayTagFilter || displayFundInvTagFilter))
      return 4;
    else if (allowFilterByGroup || displayTagFilter || displayFundInvTagFilter)
      return 6;
    return 12;
  };

  /**
   * Set the itemSearch state to filter the right side list
   */
  handleItemSearch = debounce(text => {
    this.setState({
      itemSearch: text,
      leftSelection: []
    });
  }, 500);

  /**
   * Set the selectedItemSearch state to filter the left side list
   */
  handleSelectedItemSearch = debounce(text => {
    this.setState({
      selectedItemSearch: text,
      rightSelection: []
    });
  }, 500);
}

DoubleListArrayInput.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.object),
  classes: PropTypes.object,
  className: PropTypes.string,
  input: PropTypes.object,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  meta: PropTypes.object,
  options: PropTypes.object,
  optionText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element
  ]).isRequired,
  optionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    .isRequired,
  disableValue: PropTypes.string,
  resource: PropTypes.string,
  source: PropTypes.string,
  translate: PropTypes.func,
  translateChoice: PropTypes.bool,
  readOnly: PropTypes.bool
};

DoubleListArrayInput.defaultProps = {
  classes: {},
  choices: [],
  options: {},
  optionText: 'name',
  optionValue: 'id',
  translateChoice: true,
  disableValue: 'disabled',
  readOnly: false
};

export default addField(
  translate(withStyles(doubleListStyles)(DoubleListArrayInput))
);
